import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { getApiBaseUrl, getTilesApiBaseUrl } from "./utils";

import type { EnvDataType } from "src/ts-types/ApiTypes";
import type { MeApiResponse } from "@repo/ping-react-js";
import type { RootState } from "services/store";
import {
  AddAnnotationRequest,
  AddAnnotationResponse,
  AnnotationData,
  BulkAnnotationRequest,
  BulkAnnotationResponse,
  getHttpHeaders,
  GeocoderType,
  transformBuildingToGeocodingSources,
} from "@repo/ping-react-js";
interface PDFStatusResponse {
  status: "PENDING" | "STARTED" | "SUCCESS" | "FAILURE" | "REVOKED" | "RETRY";
  task_id: string;
  document_id?: string;
  message: string;
  progress: number; // 0-100 percentage
  filename?: string;
}

export interface ClientUsageResponse {
  scope: "user" | "org";
  subject: string;
  all_sources: boolean;
  sources: Record<
    string,
    {
      credits_per_location: number;
      locations_remaining?: { daily?: number; monthly?: number };
    }
  >;
  credits: {
    start: string;
    resets_at: string | null;
    included: number;
    payg: number;
    used: number;
    remaining: number;
  } | null;
}

export const api = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: getApiBaseUrl(),
    prepareHeaders: (headers, { getState }) => {
      headers.set("Content-Type", "application/json");
      const state = getState() as RootState;
      const accessToken = state.auth.accessToken;
      if (accessToken && !headers.has("Authorization")) {
        headers.set("Authorization", `Bearer ${accessToken}`);
      }

      return headers;
    },
    credentials: "include",
  }),
  // The `SOVActivityLog` tag is used to clear the cache for the data grid on
  // the activity page when the user changes the search filters or terms. See
  // `salSlice.ts` for more information.
  tagTypes: ["SOVActivityLog", "Me", "ClientUsage", "catModeling", "policyTermsVariants"],

  endpoints: (build) => ({
    getEnv: build.query<EnvDataType, { sovid: string; readOnlyToken?: string; accessToken?: string }>({
      query: ({ sovid, accessToken, readOnlyToken }) => {
        const params = new URLSearchParams();

        if (sovid) {
          params.set("sovid", sovid);
        }

        return {
          url: `api/v1/environment?${params.toString()}`,
          method: "GET",
          headers: getHttpHeaders(accessToken, readOnlyToken),
        };
      },
    }),
    getLayerCookies: build.query<{ ok: boolean }, { sovid?: string; readOnlyToken?: string; accessToken?: string }>({
      query: ({ sovid, accessToken, readOnlyToken }) => {
        const params = new URLSearchParams();
        if (sovid) {
          params.set("sovid", sovid);
        }
        return {
          url: `${getTilesApiBaseUrl()}api/v1/layer_cookies?${params.toString()}`,
          method: "GET",
          headers: getHttpHeaders(accessToken, readOnlyToken),
        };
      },
    }),
    getMe: build.query<MeApiResponse, { accessToken: string }>({
      query: ({ accessToken }) => {
        return {
          url: "api/v1/me",
          method: "GET",
          headers: getHttpHeaders(accessToken, null),
        };
      },
      providesTags: ["Me"],
    }),
    getClientUsage: build.query<ClientUsageResponse, { accessToken: string }>({
      query: ({ accessToken }) => {
        return {
          url: "api/v1/pli/usage",
          method: "GET",
          headers: getHttpHeaders(accessToken, null),
        };
      },
      providesTags: ["ClientUsage"],
    }),
    updateAnalyticsConsent: build.mutation<
      {
        analytics_consent: boolean;
        analytics_consent_version: string;
        analytics_consent_updated_at: string;
      },
      { analyticsConsent: boolean; accessToken: string }
    >({
      query: ({ analyticsConsent, accessToken }) => ({
        url: "api/v1/me/analytics-consent",
        method: "PATCH",
        headers: getHttpHeaders(accessToken, null),
        body: { analytics_consent: analyticsConsent },
      }),
      invalidatesTags: ["Me"],
    }),
    getSettings: build.query<
      any,
      {
        readOnlyToken?: string;
        tableConfigs: Record<string, any>;
        accessToken?: string;
      }
    >({
      query: ({ tableConfigs, readOnlyToken, accessToken }) => {
        const params = new URLSearchParams(tableConfigs);

        return {
          url: `api/v1/settings?${params}`,
          method: "GET",
          headers: getHttpHeaders(accessToken, readOnlyToken),
        };
      },
    }),
    updateSettings: build.mutation<any, unknown>({
      query: ({ data }) => {
        return {
          url: `api/v1/settings`,
          method: "POST",
          body: { ...data },
        };
      },
    }),
    getAnnotation: build.query<
      AnnotationData,
      {
        sovid: string | undefined;
        readOnlyToken?: string;
        accessToken?: string;
      }
    >({
      query: ({ sovid, accessToken, readOnlyToken }) => ({
        url: `api/v1/maps/annotations/${sovid}`,
        method: "GET",
        headers: getHttpHeaders(accessToken, readOnlyToken),
      }),
    }),
    addAnnotation: build.mutation<AddAnnotationResponse, { data: AddAnnotationRequest }>({
      query: ({ data }) => {
        return {
          url: `api/v1/maps/annotations`,
          method: "POST",
          body: { ...data },
        };
      },
    }),
    bulkAddAnnotations: build.mutation<BulkAnnotationResponse, { data: BulkAnnotationRequest }>({
      query: ({ data }) => {
        return {
          url: `api/v1/maps/annotations/bulk`,
          method: "POST",
          body: { ...data },
        };
      },
    }),
    getBuildingGeocodingSources: build.query<
      GeocoderType[],
      {
        itemKey: string | undefined;
      }
    >({
      query: ({ itemKey }) => {
        return {
          url: `/api/v1/building/${encodeURIComponent(itemKey || "")}?fields=all&fields=sources&referrer=pingmaps`,
          method: "GET",
        };
      },
      transformResponse: (result: any) => {
        return transformBuildingToGeocodingSources(result);
      },
    }),
    generatePLIReport: build.mutation<
      {
        task_id?: string;
        message?: string;
        document_id?: string;
        filename?: string;
      },
      {
        force_create?: boolean;
        pli_id?: string;
        item_key?: string;
        layer?: string;
        // The active layer's /map-overlay params for the server-side capture (e.g. Past Storms' filters).
        overlay_params?: Record<string, string>;
      }
    >({
      query: ({ force_create, pli_id, item_key, layer, overlay_params }) => ({
        url: `api/v1/pli/pdf`,
        method: "POST",
        body: { force_create, pli_id, item_key, layer, overlay_params },
        timeout: 600000,
      }),
    }),
    downloadSummaryReport: build.query<Blob, { filename: string }>({
      query: ({ filename }) => {
        // Check if this is a PLI report (based on filename)
        const isPliReport = filename && filename.startsWith("pli_report");

        // Use the correct endpoint based on report type
        const endpoint = isPliReport ? "api/v1/pli/file" : "api/v1/file";

        return {
          url: `${getApiBaseUrl().replace(/\/+$/, "")}/${endpoint}/${filename}`,
          method: "GET",
          headers: {
            Accept: "application/pdf",
          },
          credentials: "include",
          mode: "cors", // Add explicit CORS mode
          timeout: 600000, // Match the generation timeout (10 minutes)
          responseHandler: async (response) => {
            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(`Download failed: ${response.status} ${response.statusText} - ${errorText}`);
            }
            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/pdf")) {
              throw new Error(`Invalid content type: ${contentType}`);
            }
            return response.blob();
          },
          cache: "no-cache",
        };
      },
    }),
    checkLocationReportStatus: build.query<
      PDFStatusResponse,
      {
        taskId?: string | null;
        documentId?: string | null;
        filename?: string | null;
        pli_id?: string;
        item_key?: string;
      }
    >({
      query: ({ taskId, documentId, filename, pli_id, item_key }) => {
        const params: Record<string, string> = {};
        if (taskId) params.task_id = taskId;
        if (documentId) params.document_id = documentId;
        if (filename) params.filename = filename;
        if (pli_id) params.pli_id = pli_id;
        if (item_key) params.item_key = item_key;

        return {
          url: `api/v1/pli/pdf`,
          method: "GET",
          params,
        };
      },
      transformResponse: (response: any) => response,
      keepUnusedDataFor: 0,
    }),
    generateLocationReport: build.mutation<
      {
        task_id?: string;
        message?: string;
        document_id?: string;
        filename?: string;
      },
      {
        sovid: string;
        map_bounds?: {
          north: number;
          south: number;
          east: number;
          west: number;
        } | null;
        highlight_bounds?: {
          north: number;
          south: number;
          east: number;
          west: number;
        } | null;
        map_type?: string;
        layer?: string | null;
        map_image_base64?: string | null;
        deck_overlay_base64?: string | null;
        measurement_tools?: {
          tool: "dtc" | "area" | "line" | "elevation" | "elevation-profile";
          label: string;
          value: string;
          points: { lat: number; lng: number }[];
          series?: { distance_ft: number; elevation_ft: number }[];
        }[];
        overlay_params?: Record<string, string>;
      }
    >({
      query: ({
        sovid,
        map_bounds,
        highlight_bounds,
        map_type,
        layer,
        map_image_base64,
        deck_overlay_base64,
        measurement_tools,
        overlay_params,
      }) => ({
        url: "api/v1/pli/report",
        method: "POST",
        body: {
          sovid,
          map_bounds,
          highlight_bounds,
          map_type,
          layer,
          map_image_base64,
          deck_overlay_base64,
          measurement_tools,
          overlay_params,
        },
        timeout: 30000,
      }),
    }),
    downloadLocationFile: build.query<Blob, { filename: string }>({
      query: ({ filename }) => ({
        url: `${getApiBaseUrl().replace(/\/+$/, "")}/api/v1/pli/file/${filename}`,
        method: "GET",
        headers: { Accept: "application/pdf" },
        credentials: "include",
        timeout: 60000,
        responseHandler: async (response) => {
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Download failed: ${response.status} ${response.statusText} - ${errorText}`);
          }
          return response.blob();
        },
        cache: "no-cache",
      }),
    }),
  }),
});

export const {
  useGetMeQuery,
  useGetClientUsageQuery,
  useUpdateAnalyticsConsentMutation,
  useAddAnnotationMutation,
  useBulkAddAnnotationsMutation,
  useGetAnnotationQuery,
  useGetEnvQuery,
  useGetLayerCookiesQuery,
  useGetBuildingGeocodingSourcesQuery,
  useGetSettingsQuery,
  useUpdateSettingsMutation,
  useGeneratePLIReportMutation,
  useLazyDownloadSummaryReportQuery,
  useLazyCheckLocationReportStatusQuery,
  useGenerateLocationReportMutation,
  useLazyDownloadLocationFileQuery,
} = api;
